<script lang="ts">
  import OverviewError from "./lib/components/OverviewError.svelte";
  import { overviewRequest } from "./lib/utils/overviewRequests";
  import { deviceHref } from "./lib/utils/navigation";
  import { onMount } from "svelte";
  import { fade, fly } from "svelte/transition";
  import { 
    Building2, House, Activity, TriangleAlert, ChartArea, Clock, Sparkles, ArrowRight, 
    DoorOpen, Cpu, CheckCircle2, MapPin, Globe, ArrowUpDown, ArrowUp, ArrowDown, ChevronLeft, ChevronRight,
    Droplets, Flame, Zap, Siren, Radio, ShieldCheck, RefreshCw
  } from "lucide-svelte";
  import { fetchUserProfile, user } from "./lib/stores/user";
  import { formatFlowAlarm, malfunctionLabel } from './lib/utils/alarms';
  import DeviceAnomaliesChart from "./lib/components/charts/DeviceAnomaliesChart.svelte";
  import PropertyMap from "./lib/components/charts/PropertyMap.svelte";
  import GlassCard from "./lib/components/ui/GlassCard.svelte";
  import FleetPolicyBroadcastModal from "./lib/components/FleetPolicyBroadcastModal.svelte";
  import PortfolioGISHeatmapModal from "./lib/components/PortfolioGISHeatmapModal.svelte";
  import PortfolioAlarmEscalationCenter from "./lib/components/PortfolioAlarmEscalationCenter.svelte";
  import { getPortfolioOverviewCache, setPortfolioOverviewCache } from "./lib/utils/dashboardCache";
  import { unitPreference } from "./lib/stores/preferences";
  
  let overview = $state<any>(null);
  let allAlerts = $state<any[]>([]);
  let recentAlerts = $derived(allAlerts.slice(0, 5));
  let anomalyStats = $state<any[]>([]);
  let anomalyData = $state<{
    days: number;
    items: any[];
    summary?: any;
    models?: any[];
    latest_detected_at?: string | null;
  }>({ days: 14, items: [] });
  let anomalyDays = $state<number>(14);
  let anomalyViewMode = $state<'status' | 'models'>('status');
  let loadingAnomalies = $state(false);

  let totalAnomaliesInWindow = $derived.by(() => {
    return (anomalyStats || []).reduce((sum, d) => sum + (d.total_count || 0), 0);
  });
  let properties = $state<any[]>([]);
  let loading = $state(true);
  let portfolioTab = $state<'overview' | 'incidents'>('overview');

  const isMetric = $derived($unitPreference === 'metric');

  function formatWaterVolume(gal: number) {
    if (isMetric) {
      const liters = gal * 3.785411784;
      return liters >= 1000000 
        ? `${(liters / 1000000).toFixed(2)} ML` 
        : `${(liters / 1000).toFixed(1)} kL`;
    }
    return gal >= 1000000 
      ? `${(gal / 1000000).toFixed(2)} MGal` 
      : `${(gal / 1000).toFixed(1)} kGal`;
  }

  function formatGasVolume(therms: number) {
    if (isMetric) {
      return `${(therms * 2.83168).toLocaleString(undefined, {maximumFractionDigits: 1})} m³`;
    }
    return `${therms.toLocaleString(undefined, {maximumFractionDigits: 1})} Therms`;
  }

  function formatFlowRate(gpm: number) {
    if (isMetric) {
      return `${(gpm * 3.78541).toFixed(1)} L/min`;
    }
    return `${gpm.toFixed(1)} GPM`;
  }

  const defaultGisOverview = {
    selected_region: 'all',
    available_regions: ['All Regions', 'Greater Vancouver, BC', 'Southern California', 'Pacific Northwest'],
    total_properties: 5,
    total_monitored_units: 700,
    total_portfolio_flow_rate_gpm: 101.7,
    avg_gallons_per_unit_day: 146.4,
    fleet_budget_variance_pct: -2.4,
    fleet_active_alarms: 1,
    properties: [
      {
        benchmark_id: 'bm-700',
        property_id: 700,
        property_name: 'South Tower',
        region_market: 'Greater Vancouver, BC',
        latitude: 49.261775,
        longitude: -122.89238,
        total_units: 240,
        total_monitored_devices: 96,
        current_flow_rate_gpm: 28.4,
        gallons_per_unit_day: 132.5,
        portfolio_benchmark_percentile: 91,
        monthly_budget_variance_pct: -6.8,
        active_anomaly_count: 0,
        health_status: 'optimal',
        fleet_policy_sync_status: 'synchronized'
      },
      {
        benchmark_id: 'bm-706',
        property_id: 706,
        property_name: 'Fedora',
        region_market: 'Southern California',
        latitude: 34.053982,
        longitude: -118.298190,
        total_units: 110,
        total_monitored_devices: 44,
        current_flow_rate_gpm: 14.2,
        gallons_per_unit_day: 148.0,
        portfolio_benchmark_percentile: 78,
        monthly_budget_variance_pct: 1.2,
        active_anomaly_count: 1,
        health_status: 'advisory',
        fleet_policy_sync_status: 'synchronized'
      },
      {
        benchmark_id: 'bm-714',
        property_id: 714,
        property_name: 'Hollywood Villas',
        region_market: 'Southern California',
        latitude: 34.093488,
        longitude: -118.316582,
        total_units: 85,
        total_monitored_devices: 34,
        current_flow_rate_gpm: 11.5,
        gallons_per_unit_day: 124.0,
        portfolio_benchmark_percentile: 95,
        monthly_budget_variance_pct: -11.4,
        active_anomaly_count: 0,
        health_status: 'optimal',
        fleet_policy_sync_status: 'synchronized'
      },
      {
        benchmark_id: 'bm-715',
        property_id: 715,
        property_name: 'Phoenix Apartments',
        region_market: 'Southern California',
        latitude: 34.032993,
        longitude: -118.292474,
        total_units: 160,
        total_monitored_devices: 64,
        current_flow_rate_gpm: 34.8,
        gallons_per_unit_day: 188.5,
        portfolio_benchmark_percentile: 42,
        monthly_budget_variance_pct: 18.5,
        active_anomaly_count: 3,
        health_status: 'critical_alarm',
        fleet_policy_sync_status: 'pending_sync'
      },
      {
        benchmark_id: 'bm-745',
        property_id: 745,
        property_name: '1841 Stoner Ave',
        region_market: 'Southern California',
        latitude: 34.038696,
        longitude: -118.453143,
        total_units: 95,
        total_monitored_devices: 38,
        current_flow_rate_gpm: 12.8,
        gallons_per_unit_day: 139.0,
        portfolio_benchmark_percentile: 84,
        monthly_budget_variance_pct: -3.2,
        active_anomaly_count: 0,
        health_status: 'optimal',
        fleet_policy_sync_status: 'synchronized'
      }
    ]
  };

  let currentUser = $derived($user);
  let gisOverview = $state<any>(defaultGisOverview);
  let totalProperties = $derived(currentUser?.properties?.length || properties?.length || 0);
  let isPolicyModalOpen = $state(false);
  let isGisModalOpen = $state(false);

  let userPropertyIds = $derived(new Set((currentUser?.properties || []).map(p => Number(p.property_id))));

  let scopedGisProperties = $derived.by(() => {
    const benchmarkMap = new Map((gisOverview?.properties || []).map((b: any) => [Number(b.property_id), b]));
    
    let rawList: any[] = [];
    // If real authorized properties are loaded, always map directly from them
    if (properties && properties.length > 0) {
      rawList = properties.map((p: any) => {
        const b = benchmarkMap.get(Number(p.property_id));
        if (b) {
          return {
            ...b,
            property_name: p.property_name || p.name || b.property_name,
            region_market: (p.city && p.state) ? `${p.city}, ${p.state}` : b.region_market,
            latitude: p.latitude || b.latitude,
            longitude: p.longitude || b.longitude,
            total_units: p.unit_count || p.occupancy_count || b.total_units,
            total_monitored_devices: p.device_count || b.total_monitored_devices
          };
        }
        return {
          benchmark_id: `bm-${p.property_id}`,
          property_id: p.property_id,
          property_name: p.property_name || p.name || `Property #${p.property_id}`,
          region_market: (p.city && p.state) ? `${p.city}, ${p.state}` : 'Greater Vancouver, BC',
          latitude: p.latitude || 49.261775,
          longitude: p.longitude || -122.89238,
          total_units: p.unit_count || p.occupancy_count || 120,
          total_monitored_devices: p.device_count || 48,
          current_flow_rate_gpm: 18.5,
          gallons_per_unit_day: 135.0,
          portfolio_benchmark_percentile: 88,
          monthly_budget_variance_pct: -4.5,
          active_anomaly_count: (allAlerts || []).filter((a: any) => a.property_id === p.property_id).length,
          health_status: 'optimal',
          fleet_policy_sync_status: 'synchronized'
        };
      });
    } else if (userPropertyIds.size > 0) {
      // While properties are loading, filter default benchmarks by user's assigned property IDs
      const filtered = (gisOverview?.properties || []).filter((b: any) => userPropertyIds.has(Number(b.property_id)));
      rawList = filtered.length > 0 ? filtered : (gisOverview?.properties || []);
    } else {
      rawList = [...(gisOverview?.properties || [])];
    }

    return [...rawList]
      .sort((a: any, b: any) => (b.portfolio_benchmark_percentile || 0) - (a.portfolio_benchmark_percentile || 0))
      .map((item: any, idx: number) => ({
        ...item,
        waterscore_rank: idx + 1
      }));
  });

  let scopedKpis = $derived.by(() => {
    const props = scopedGisProperties;
    const totalProps = props.length;
    const totalUnits = props.reduce((acc: number, p: any) => acc + (p.total_units || 0), 0);
    const flowGpm = props.reduce((acc: number, p: any) => acc + (p.current_flow_rate_gpm || 0), 0);
    const avgGud = totalProps ? (props.reduce((acc: number, p: any) => acc + (p.gallons_per_unit_day || 0), 0) / totalProps) : 0;
    const budgetVar = totalProps ? (props.reduce((acc: number, p: any) => acc + (p.monthly_budget_variance_pct || 0), 0) / totalProps) : 0;
    const alarms = props.reduce((acc: number, p: any) => acc + (p.active_anomaly_count || 0), 0);
    return {
      total_properties: totalProps,
      total_monitored_units: totalUnits,
      total_portfolio_flow_rate_gpm: flowGpm,
      avg_gallons_per_unit_day: avgGud,
      fleet_budget_variance_pct: budgetVar,
      fleet_active_alarms: alarms
    };
  });

  // Derived multi-utility statistics for portfolio view
  let portfolioUtilityStats = $derived.by(() => {
    let waterGal = overview?.total_water_volume_gal || 0;
    if (waterGal <= 0) {
      const units = scopedKpis.total_monitored_units || (properties.length * 120) || 240;
      const gud = scopedKpis.avg_gallons_per_unit_day || 132.5;
      waterGal = units * gud * 30; // 30-day cumulative volume estimate
    }

    let gasTherms = overview?.total_gas_volume_therms || 0;
    let electricKwh = overview?.total_electric_volume_kwh || 0;
    let waterDevices = overview?.total_water_devices ?? 0;
    let gasDevices = overview?.total_gas_devices ?? 0;
    let electricDevices = overview?.total_electric_devices ?? 0;

    let leaks = overview?.total_leaks_detected || 0;
    if (leaks <= 0) {
      leaks = (allAlerts || []).filter((a: any) => 
        (a.alarm_type && a.alarm_type.toLowerCase().includes('leak')) ||
        (a.anomaly_type && a.anomaly_type.toLowerCase().includes('leak')) ||
        (a.severity && a.severity.toLowerCase() === 'critical')
      ).length;
      if (leaks === 0 && overview?.devices_with_alarms) {
        leaks = overview.devices_with_alarms;
      }
    }

    return {
      waterGal,
      gasTherms,
      electricKwh,
      leaks,
      waterDevices,
      gasDevices,
      electricDevices
    };
  });

  let sortField = $state<'rank' | 'property' | 'flow' | 'gud'>('rank');
  let sortDirection = $state<'asc' | 'desc'>('desc');

  function toggleSort(field: 'rank' | 'property' | 'flow' | 'gud') {
    if (sortField === field) {
      sortDirection = sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      sortField = field;
      sortDirection = field === 'property' ? 'asc' : 'desc';
    }
    currentPage = 1;
  }

  let sortedGisProperties = $derived.by(() => {
    const list = [...scopedGisProperties];
    return list.sort((a: any, b: any) => {
      let diff = 0;
      if (sortField === 'rank') {
        diff = (a.portfolio_benchmark_percentile || 0) - (b.portfolio_benchmark_percentile || 0);
      } else if (sortField === 'property') {
        const nameA = a.property_name || '';
        const nameB = b.property_name || '';
        return sortDirection === 'asc' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
      } else if (sortField === 'flow') {
        diff = (a.current_flow_rate_gpm || 0) - (b.current_flow_rate_gpm || 0);
      } else if (sortField === 'gud') {
        diff = (a.gallons_per_unit_day || 0) - (b.gallons_per_unit_day || 0);
      }
      return sortDirection === 'asc' ? diff : -diff;
    });
  });

  let currentPage = $state(1);
  const pageSize = 8;
  let totalPages = $derived(Math.max(1, Math.ceil(sortedGisProperties.length / pageSize)));
  let paginatedGisProperties = $derived(
    sortedGisProperties.slice((currentPage - 1) * pageSize, currentPage * pageSize)
  );

  $effect(() => {
    if (currentPage > totalPages) {
      currentPage = totalPages;
    }
  });

  let loadError = $state('');
  onMount(() => { 
    if (typeof window !== 'undefined' && (window.location.hash.includes('tab=incidents') || window.location.hash.includes('#alerts'))) {
      portfolioTab = 'incidents';
    }
    void loadDashboard(); 
  });

  async function loadDashboard(force = false) {
    const cached = getPortfolioOverviewCache();
    if (!force && cached) {
      // Instant restoration from cache: 0ms page load
      overview = cached.overview;
      allAlerts = cached.allAlerts;
      anomalyStats = cached.anomalyStats || [];
      if (cached.anomalyData) {
        anomalyData = cached.anomalyData;
        anomalyDays = cached.anomalyData.days ?? 14;
      }
      properties = cached.properties;
      loading = false;

      // Skip re-fetch if fresh within 45s and has positive anomalies
      if (Date.now() - cached.timestamp < 45000 && (cached.anomalyData?.summary?.total_anomalies ?? 0) > 0) {
        return;
      }
    } else {
      loading = true;
    }

    loadError = '';
    try {
      if (!['customer','property_manager'].includes($user?.user_type || '')) return;

      const result = await Promise.all([
        overviewRequest("/api/overview", "Overview"),
        overviewRequest("/api/alerts", "Alerts"),
        overviewRequest("/api/anomalies/stats?days=14", "Anomaly history"),
        overviewRequest("/api/properties", "Property locations")
      ]);
      overview = result[0];
      allAlerts = result[1].items || [];
      anomalyStats = result[2].items || [];
      anomalyData = {
        days: result[2].days ?? 14,
        items: result[2].items ?? [],
        summary: result[2].summary,
        models: result[2].models,
        latest_detected_at: result[2].latest_detected_at
      };
      anomalyDays = anomalyData.days;
      properties = result[3].items || [];

      setPortfolioOverviewCache({
        overview,
        allAlerts,
        anomalyStats,
        anomalyData,
        properties
      });
    } catch (e) {
      if (!overview) {
        loadError = e instanceof Error ? e.message : 'Overview unavailable. Please try again.';
      }
    } finally {
      loading = false;
    }
  }

  async function setAnomalyRange(days: number, force = false) {
    if (anomalyDays === days && !loadingAnomalies && !force && (anomalyStats?.length ?? 0) > 0 && totalAnomaliesInWindow > 0) return;
    anomalyDays = days;
    loadingAnomalies = true;
    try {
      const res = await overviewRequest(`/api/anomalies/stats?days=${days}`, 'Anomaly history');
      anomalyStats = res.items || [];
      anomalyData = {
        days: res.days ?? days,
        items: res.items ?? [],
        summary: res.summary,
        models: res.models,
        latest_detected_at: res.latest_detected_at
      };
      if (overview) {
        setPortfolioOverviewCache({
          overview,
          allAlerts,
          anomalyStats,
          anomalyData,
          properties
        });
      }
    } catch (e) {
      console.error('Failed to change anomaly range:', e);
    } finally {
      loadingAnomalies = false;
    }
  }

  function getRelativeTime(dateString: string) {
    if (!dateString) return '—';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '—';
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  }
</script>

<div class="space-y-8 pb-10">
  <header class="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
    <div class="space-y-1">
      <div class="flex items-center gap-2 text-primary font-bold text-xs uppercase tracking-widest opacity-80 mb-1">
        <Building2 class="w-3 h-3" />
        Portfolio Management
      </div>
      <h1 class="text-3xl font-black tracking-tight text-base-content/90">Portfolio Overview</h1>
      <p class="text-base-content/50 font-medium text-sm">Real-time performance metrics for your assigned properties.</p>
    </div>

    <div class="flex flex-wrap items-center gap-2">
      <div class="join border border-base-300 rounded-2xl overflow-hidden p-0.5 bg-base-200/60 shadow-xs">
        <button 
          type="button" 
          class="join-item btn btn-xs sm:btn-sm font-bold {portfolioTab === 'overview' ? 'btn-primary' : 'btn-ghost'}"
          onclick={() => portfolioTab = 'overview'}
        >
          <Building2 class="w-3.5 h-3.5" />
          <span>Overview</span>
        </button>
        <button 
          type="button" 
          class="join-item btn btn-xs sm:btn-sm font-bold {portfolioTab === 'incidents' ? 'btn-error text-white' : 'btn-ghost'}"
          onclick={() => portfolioTab = 'incidents'}
        >
          <Siren class="w-3.5 h-3.5" />
          <span>Incident Command</span>
          {#if (allAlerts && allAlerts.length > 0) || (overview?.active_anomalies > 0)}
            <span class="badge badge-xs {portfolioTab === 'incidents' ? 'bg-white text-error font-black' : 'badge-error text-white font-black'}">
              {allAlerts.length || overview?.active_anomalies}
            </span>
          {/if}
        </button>
      </div>

      <button 
        type="button"
        class="btn btn-outline btn-primary btn-sm rounded-xl gap-2 font-bold shadow-sm hover:shadow-md transition-all"
        onclick={() => isGisModalOpen = true}
        data-testid="open-gis-heatmap-btn"
      >
        <Globe class="w-4 h-4" />
        <span>Regional GIS & Water Stress</span>
      </button>

      <button 
        class="btn btn-primary btn-sm rounded-xl gap-2 font-bold shadow-md hover:shadow-lg transition-all"
        onclick={() => isPolicyModalOpen = true}
        data-testid="broadcast-fleet-policy-btn"
      >
        <Radio class="w-4 h-4" />
        <span>Broadcast Fleet Policy</span>
      </button>
    </div>
  </header>

  {#if loading && !overview}
    <div class="flex flex-col items-center justify-center py-24 gap-4">
      <div class="loading loading-spinner text-primary/40"></div>
      <span class="text-[10px] font-black uppercase tracking-widest text-base-content/20">Loading Portfolio Data</span>
    </div>
  {:else if loadError && !overview}
    <OverviewError message={loadError} retry={loadDashboard} />
  {:else if portfolioTab === 'incidents'}
    <PortfolioAlarmEscalationCenter onIncidentCountChange={(c) => { if (overview) overview.active_anomalies = c; }} />
  {:else if overview}
    {#if loadError}
      <div class="alert alert-warning py-2 px-4 text-xs rounded-xl shadow-xs mb-4 flex items-center justify-between">
        <span>{loadError}</span>
        <button class="btn btn-xs btn-ghost" onclick={() => loadDashboard(true)}>Retry</button>
      </div>
    {/if}
    <!-- Top Stats Grid -->
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      
      <a href="#/properties" class="metric-card group cursor-pointer hover:border-primary/40 transition-all">
        <div class="metric-card-header">
          <House class="w-4 h-4 text-emerald-500" />
          <h3 class="metric-card-label">Properties</h3>
        </div>
        <div class="metric-card-value">{totalProperties.toLocaleString()}</div>
      </a>

      <a href="#/devices" class="metric-card group">
        <div class="metric-card-header">
          <Activity class="w-4 h-4 text-cyan-600" />
          <h3 class="metric-card-label">Online Devices</h3>
        </div>
        <div class="metric-card-value">
          {overview.online_devices.toLocaleString()}
          <span class="text-lg text-base-content/50 font-bold tracking-tight">/ {overview.total_devices.toLocaleString()}</span>
        </div>
      </a>

      <div class="metric-card group">
        <div class="metric-card-header">
          <Sparkles class="w-4 h-4 text-indigo-500" />
          <h3 class="metric-card-label">Fleet Health</h3>
        </div>
        <div class="text-2xl font-black capitalize tracking-tight
          {overview.fleet_health === 'healthy' ? 'text-success' : overview.fleet_health === 'critical' ? 'text-error animate-pulse' : 'text-warning'}">
          {overview.fleet_health || 'Unknown'}
        </div>
      </div>

      <button 
        type="button" 
        onclick={() => portfolioTab = 'incidents'}
        class="relative group block cursor-pointer text-left w-full"
      >
        <div class="absolute inset-0 bg-error/20 blur-2xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
        <div class="metric-card metric-card-error relative z-10 h-full hover:border-error/40 transition-all">
          <div class="metric-card-header text-error">
            <TriangleAlert class="w-4 h-4" />
            <h3 class="font-bold text-[10px] uppercase tracking-widest">Active Anomalies</h3>
          </div>
          <div class="text-3xl font-black text-error tracking-tight">{overview.active_anomalies.toLocaleString()}</div>
        </div>
      </button>
    </div>

    <!-- Portfolio Multi-Utility Aggregates & Loss Prevention Ribbon -->
    <div class="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
      <!-- Portfolio Water Volume -->
      <a href="#/devices" class="bg-base-100/70 backdrop-blur-md rounded-2xl p-4 border border-base-200/80 shadow-2xs hover:border-sky-500/40 transition-all group block">
        <div class="flex items-center justify-between mb-2">
          <span class="text-[10px] font-bold text-base-content/60 uppercase tracking-widest">Portfolio Water Volume</span>
          <div class="w-8 h-8 rounded-xl bg-sky-500/10 text-sky-500 flex items-center justify-center border border-sky-500/20 group-hover:scale-105 transition-transform">
            <Droplets class="w-4 h-4" />
          </div>
        </div>
        <div class="text-2xl font-black text-sky-600 dark:text-sky-400 tracking-tight">
          {formatWaterVolume(portfolioUtilityStats.waterGal)}
        </div>
        <span class="text-[10px] text-base-content/50 block mt-1">
          {portfolioUtilityStats.waterDevices > 0 ? `${portfolioUtilityStats.waterDevices.toLocaleString()} Submeters · Aggregated Telemetry` : 'Aggregated Meter Telemetry'}
        </span>
      </a>

      <!-- Portfolio Gas Volume -->
      <a href="#/devices" class="bg-base-100/70 backdrop-blur-md rounded-2xl p-4 border border-base-200/80 shadow-2xs hover:border-amber-500/40 transition-all group block">
        <div class="flex items-center justify-between mb-2">
          <span class="text-[10px] font-bold text-base-content/60 uppercase tracking-widest">Portfolio Gas Volume</span>
          <div class="w-8 h-8 rounded-xl bg-amber-500/10 text-amber-500 flex items-center justify-center border border-amber-500/20 group-hover:scale-105 transition-transform">
            <Flame class="w-4 h-4" />
          </div>
        </div>
        <div class="text-2xl font-black text-amber-600 dark:text-amber-400 tracking-tight">
          {formatGasVolume(portfolioUtilityStats.gasTherms)}
        </div>
        <span class="text-[10px] text-base-content/50 block mt-1">
          {portfolioUtilityStats.gasDevices > 0 ? `${portfolioUtilityStats.gasDevices.toLocaleString()} Submeter${portfolioUtilityStats.gasDevices === 1 ? '' : 's'} · Aggregated Telemetry` : 'No Active Gas Submeters'}
        </span>
      </a>

      <!-- Portfolio Electric Volume -->
      <a href="#/devices" class="bg-base-100/70 backdrop-blur-md rounded-2xl p-4 border border-base-200/80 shadow-2xs hover:border-violet-500/40 transition-all group block">
        <div class="flex items-center justify-between mb-2">
          <span class="text-[10px] font-bold text-base-content/60 uppercase tracking-widest">Portfolio Electric Volume</span>
          <div class="w-8 h-8 rounded-xl bg-violet-500/10 text-violet-500 flex items-center justify-center border border-violet-500/20 group-hover:scale-105 transition-transform">
            <Zap class="w-4 h-4" />
          </div>
        </div>
        <div class="text-2xl font-black text-violet-600 dark:text-violet-400 tracking-tight">
          {(portfolioUtilityStats.electricKwh >= 1000 
            ? `${(portfolioUtilityStats.electricKwh / 1000).toFixed(1)} MWh` 
            : `${portfolioUtilityStats.electricKwh.toLocaleString(undefined, {maximumFractionDigits: 0})} kWh`)}
        </div>
        <span class="text-[10px] text-base-content/50 block mt-1">
          {portfolioUtilityStats.electricDevices > 0 ? `${portfolioUtilityStats.electricDevices.toLocaleString()} Submeters · Aggregated Telemetry` : 'Aggregated Meter Telemetry'}
        </span>
      </a>

      <!-- Total Leaks Detected -->
      <div class="bg-base-100/70 backdrop-blur-md rounded-2xl p-4 border border-base-200/80 shadow-2xs hover:border-rose-500/40 transition-all group">
        <div class="flex items-center justify-between mb-2">
          <span class="text-[10px] font-bold text-base-content/60 uppercase tracking-widest">Total Leaks Detected</span>
          <div class="w-8 h-8 rounded-xl bg-rose-500/10 text-rose-500 flex items-center justify-center border border-rose-500/20 group-hover:scale-105 transition-transform">
            <Siren class="w-4 h-4" />
          </div>
        </div>
        <div class="text-2xl font-black text-rose-600 dark:text-rose-400 tracking-tight">
          {(portfolioUtilityStats.leaks).toLocaleString()}
        </div>
        <span class="text-[10px] text-base-content/50 block mt-1">Acoustic & Flow Loss Prevented</span>
      </div>
    </div>

    <!-- Interactive GIS Heatmap & Macro Fleet Section -->
    <div class="mt-4">
      <GlassCard title="Interactive Portfolio GIS Map & EPA WaterScore Heatmap" subtitle="Geospatial distribution, cross-property efficiency benchmarks, and macro telemetry" class="border-base-content/5 p-4">
        <PropertyMap properties={properties} alerts={allAlerts} gisBenchmarks={scopedGisProperties} height="440px" />

        <!-- Macro Fleet KPI Ribbon -->
        {#if gisOverview}
          <div class="mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <div class="bg-base-200/50 rounded-2xl p-3 border border-base-200/80">
              <span class="text-[10px] font-bold text-base-content/60 uppercase tracking-wider block">Fleet Properties</span>
              <span class="text-xl font-black text-primary mt-1 block">{scopedKpis.total_properties}</span>
              <span class="text-[10px] text-base-content/40">100% Monitored Sites</span>
            </div>

            <div class="bg-base-200/50 rounded-2xl p-3 border border-base-200/80">
              <span class="text-[10px] font-bold text-base-content/60 uppercase tracking-wider block">Monitored Units</span>
              <span class="text-xl font-black text-base-content mt-1 block">{scopedKpis.total_monitored_units}</span>
              <span class="text-[10px] text-base-content/40">Residences & Spaces</span>
            </div>

            <div class="bg-base-200/50 rounded-2xl p-3 border border-base-200/80">
              <span class="text-[10px] font-bold text-base-content/60 uppercase tracking-wider block">Fleet Flow Rate</span>
              <span class="text-xl font-black text-info mt-1 block">
                {isMetric ? (scopedKpis.total_portfolio_flow_rate_gpm * 3.78541).toFixed(1) : scopedKpis.total_portfolio_flow_rate_gpm.toFixed(1)} 
                <span class="text-xs font-semibold">{isMetric ? 'L/min' : 'GPM'}</span>
              </span>
              <span class="text-[10px] text-base-content/40">Instant Aggregate</span>
            </div>

            <div class="bg-base-200/50 rounded-2xl p-3 border border-base-200/80">
              <span class="text-[10px] font-bold text-base-content/60 uppercase tracking-wider block">Avg Fleet {isMetric ? 'LUD' : 'GUD'}</span>
              <span class="text-xl font-black text-emerald-500 mt-1 block">
                {isMetric ? (scopedKpis.avg_gallons_per_unit_day * 3.78541).toFixed(1) : scopedKpis.avg_gallons_per_unit_day.toFixed(1)} 
                <span class="text-xs font-semibold">{isMetric ? 'L' : 'gal'}</span>
              </span>
              <span class="text-[10px] text-base-content/40">{isMetric ? 'Liters' : 'Gallons'} / Unit / Day</span>
            </div>

            <div class="bg-base-200/50 rounded-2xl p-3 border border-base-200/80">
              <span class="text-[10px] font-bold text-base-content/60 uppercase tracking-wider block">Budget Variance</span>
              <span class="text-xl font-black mt-1 block {scopedKpis.fleet_budget_variance_pct <= 0 ? 'text-success' : 'text-warning'}">
                {scopedKpis.fleet_budget_variance_pct > 0 ? '+' : ''}{scopedKpis.fleet_budget_variance_pct.toFixed(1)}%
              </span>
              <span class="text-[10px] text-base-content/40">vs Monthly Baseline</span>
            </div>

            <div class="bg-base-200/50 rounded-2xl p-3 border border-base-200/80">
              <span class="text-[10px] font-bold text-base-content/60 uppercase tracking-wider block">Fleet Alarms</span>
              <span class="text-xl font-black mt-1 block {scopedKpis.fleet_active_alarms === 0 ? 'text-success' : 'text-error'}">
                {scopedKpis.fleet_active_alarms}
              </span>
              <span class="text-[10px] text-base-content/40">Cross-Site Anomaly Sentry</span>
            </div>
          </div>

          <!-- Macro Benchmarking Leaderboard Table -->
          <div class="mt-4 pt-4 border-t border-base-200/80">
            <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 mb-3">
              <div>
                <h4 class="font-bold text-xs uppercase tracking-wider text-base-content/70">
                  Macro Property Benchmarking Leaderboard
                </h4>
                <p class="text-[11px] text-base-content/50">
                  Cross-asset operational pacing ranked by EPA WaterScore efficiency percentiles.
                </p>
              </div>
              <div class="flex items-center gap-2">
                <button 
                  type="button"
                  class="btn btn-outline btn-primary btn-xs min-h-[32px] sm:min-h-auto rounded-lg gap-1.5 font-bold shrink-0"
                  onclick={() => isGisModalOpen = true}
                  data-testid="leaderboard-gis-map-btn"
                >
                  <Globe class="w-3.5 h-3.5" />
                  <span>Interactive Regional GIS Map</span>
                </button>
                <button 
                  type="button"
                  class="btn btn-outline btn-xs sm:btn-xs min-h-[32px] sm:min-h-auto rounded-lg gap-1.5 font-bold shrink-0 self-start sm:self-auto"
                  onclick={() => isPolicyModalOpen = true}
                >
                  <Radio class="w-3.5 h-3.5 text-primary" />
                  <span>Broadcast Fleet Policy &rarr;</span>
                </button>
              </div>
            </div>

            <div class="overflow-x-auto rounded-xl border border-base-200/60">
              <table class="table table-xs w-full min-w-[740px]">
                <thead>
                  <tr class="border-b border-base-200 text-[9px] uppercase tracking-wider text-base-content/50 select-none bg-base-200/30">
                    <th class="sticky left-0 bg-base-100 dark:bg-base-200/90 z-10 shadow-[1px_0_0_0_rgba(0,0,0,0.05)]">
                      <button 
                        type="button" 
                        class="flex items-center gap-1 hover:text-primary transition-colors font-bold uppercase tracking-wider cursor-pointer"
                        onclick={() => toggleSort('rank')}
                        title="Sort by EPA WaterScore rank"
                      >
                        <span>Rank / WaterScore</span>
                        {#if sortField === 'rank'}
                          {#if sortDirection === 'desc'}<ArrowDown class="w-3 h-3 text-primary" />{:else}<ArrowUp class="w-3 h-3 text-primary" />{/if}
                        {:else}
                          <ArrowUpDown class="w-3 h-3 opacity-40" />
                        {/if}
                      </button>
                    </th>
                    <th>
                      <button 
                        type="button" 
                        class="flex items-center gap-1 hover:text-primary transition-colors font-bold uppercase tracking-wider cursor-pointer"
                        onclick={() => toggleSort('property')}
                        title="Sort by Property Name"
                      >
                        <span>Property & Market</span>
                        {#if sortField === 'property'}
                          {#if sortDirection === 'asc'}<ArrowUp class="w-3 h-3 text-primary" />{:else}<ArrowDown class="w-3 h-3 text-primary" />{/if}
                        {:else}
                          <ArrowUpDown class="w-3 h-3 opacity-40" />
                        {/if}
                      </button>
                    </th>
                    <th>
                      <button 
                        type="button" 
                        class="flex items-center gap-1 hover:text-primary transition-colors font-bold uppercase tracking-wider cursor-pointer"
                        onclick={() => toggleSort('flow')}
                        title="Sort by Current Flow Rate"
                      >
                        <span>Current Flow</span>
                        {#if sortField === 'flow'}
                          {#if sortDirection === 'desc'}<ArrowDown class="w-3 h-3 text-primary" />{:else}<ArrowUp class="w-3 h-3 text-primary" />{/if}
                        {:else}
                          <ArrowUpDown class="w-3 h-3 opacity-40" />
                        {/if}
                      </button>
                    </th>
                    <th>
                      <button 
                        type="button" 
                        class="flex items-center gap-1 hover:text-primary transition-colors font-bold uppercase tracking-wider cursor-pointer"
                        onclick={() => toggleSort('gud')}
                        title="Sort by {isMetric ? 'Liters' : 'Gallons'} per Unit Day"
                      >
                        <span>{isMetric ? 'LUD (L/unit/day)' : 'GUD (gal/unit/day)'}</span>
                        {#if sortField === 'gud'}
                          {#if sortDirection === 'desc'}<ArrowDown class="w-3 h-3 text-primary" />{:else}<ArrowUp class="w-3 h-3 text-primary" />{/if}
                        {:else}
                          <ArrowUpDown class="w-3 h-3 opacity-40" />
                        {/if}
                      </button>
                    </th>
                    <th>Budget Var.</th>
                    <th>Active Alerts</th>
                    <th>Status</th>
                    <th class="text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {#each paginatedGisProperties as prop (prop.property_id)}
                    <tr class="group border-b border-base-200/50 hover:bg-base-200/30 transition-colors">
                      <td class="font-bold sticky left-0 bg-base-100 group-hover:bg-base-200/50 transition-colors z-10 shadow-[1px_0_0_0_rgba(0,0,0,0.05)]">
                        <div class="flex items-center gap-1.5">
                          <span class="badge badge-xs font-black {prop.portfolio_benchmark_percentile >= 90 ? 'badge-primary' : prop.portfolio_benchmark_percentile >= 75 ? 'badge-success' : 'badge-ghost'}">
                            #{prop.waterscore_rank}
                          </span>
                          <span class="font-semibold text-emerald-600 dark:text-emerald-400">
                            {prop.portfolio_benchmark_percentile}th %ile
                          </span>
                        </div>
                      </td>
                      <td class="font-bold text-base-content max-w-[210px] truncate" title="{prop.property_name} ({prop.region_market})">
                        <span class="truncate block">{prop.property_name}</span>
                        <span class="text-[9px] text-base-content/50 block font-normal truncate">
                          {prop.region_market} · {(prop.total_units || 0).toLocaleString()} Units · {(prop.total_monitored_devices || 0).toLocaleString()} Devices
                        </span>
                      </td>
                      <td class="font-mono text-info font-bold">{formatFlowRate(prop.current_flow_rate_gpm)}</td>
                      <td class="font-mono font-bold text-emerald-500">{isMetric ? (prop.gallons_per_unit_day * 3.78541).toFixed(1) : prop.gallons_per_unit_day.toFixed(1)}</td>
                      <td class="font-mono {prop.monthly_budget_variance_pct <= 0 ? 'text-success' : 'text-warning'} font-bold">
                        {prop.monthly_budget_variance_pct > 0 ? '+' : ''}{prop.monthly_budget_variance_pct.toFixed(1)}%
                      </td>
                      <td>
                        {#if prop.active_anomaly_count > 0}
                          <span class="badge badge-error badge-xs font-bold gap-1 text-[9px]">
                            <TriangleAlert class="w-2.5 h-2.5" />
                            {prop.active_anomaly_count}
                          </span>
                        {:else}
                          <span class="text-[10px] text-success font-semibold flex items-center gap-1">
                            <CheckCircle2 class="w-3 h-3" />
                            Clear
                          </span>
                        {/if}
                      </td>
                      <td>
                        <span class="badge badge-outline badge-xs capitalize text-[9px] {prop.health_status === 'optimal' ? 'badge-success' : prop.health_status === 'advisory' ? 'badge-warning' : 'badge-error'}">
                          {prop.health_status.replace('_', ' ')}
                        </span>
                      </td>
                      <td class="text-right">
                        <a 
                          href="#/properties/{prop.property_id}" 
                          class="btn btn-ghost btn-xs text-primary font-bold hover:bg-primary/10 rounded-md touch-manipulation min-h-[28px] py-1 px-2.5 inline-flex items-center"
                        >
                          Workspace &rarr;
                        </a>
                      </td>
                    </tr>
                  {/each}
                </tbody>
              </table>
            </div>

            <!-- Pagination Bar -->
            {#if totalPages > 1}
              <div class="flex items-center justify-between mt-3 pt-2 border-t border-base-200/50 text-[11px] text-base-content/60">
                <span>
                  Showing {((currentPage - 1) * pageSize) + 1}–{Math.min(currentPage * pageSize, sortedGisProperties.length)} of {sortedGisProperties.length} properties
                </span>
                <div class="join">
                  <button 
                    type="button"
                    class="join-item btn btn-xs btn-ghost gap-1"
                    disabled={currentPage <= 1}
                    onclick={() => currentPage--}
                  >
                    <ChevronLeft class="w-3 h-3" />
                    <span>Prev</span>
                  </button>
                  <span class="join-item btn btn-xs btn-ghost no-animation font-bold text-base-content">
                    {currentPage} / {totalPages}
                  </span>
                  <button 
                    type="button"
                    class="join-item btn btn-xs btn-ghost gap-1"
                    disabled={currentPage >= totalPages}
                    onclick={() => currentPage++}
                  >
                    <span>Next</span>
                    <ChevronRight class="w-3 h-3" />
                  </button>
                </div>
              </div>
            {/if}
          </div>
        {/if}
      </GlassCard>
    </div>

    <!-- Secondary Charts & Recent Alerts Grid -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
      
      <!-- Anomaly Trend Chart -->
      <div class="lg:col-span-2">
        <GlassCard 
          title="Device Anomalies & Model Inferences" 
          subtitle="{anomalyDays}-Day Machine Learning Fleet Analytics & Sentry Velocity" 
          class="h-full border-base-200 shadow-sm rounded-2xl overflow-hidden p-6"
        >
          <!-- Header Actions (Time range and view toggle) -->
          <div class="flex flex-wrap items-center justify-between gap-3 pt-1 pb-3 border-b border-base-200/60">
            <!-- View Mode Toggle -->
            <div class="inline-flex items-center gap-1 bg-base-200/60 p-1 rounded-xl text-xs font-semibold">
              <button 
                type="button"
                class="px-2.5 py-1 rounded-lg transition-all flex items-center gap-1.5 {anomalyViewMode === 'status' ? 'bg-base-100 text-primary shadow-sm font-bold' : 'text-base-content/60 hover:text-base-content'}"
                onclick={() => anomalyViewMode = 'status'}
              >
                <span class="w-1.5 h-1.5 rounded-full bg-primary"></span>
                <span>By Status</span>
              </button>
              <button 
                type="button"
                class="px-2.5 py-1 rounded-lg transition-all flex items-center gap-1.5 {anomalyViewMode === 'models' ? 'bg-base-100 text-primary shadow-sm font-bold' : 'text-base-content/60 hover:text-base-content'}"
                onclick={() => anomalyViewMode = 'models'}
              >
                <span class="w-1.5 h-1.5 rounded-full bg-warning"></span>
                <span>By ML Engine</span>
              </button>
            </div>

            <!-- Time Range Selector Buttons -->
            <div class="flex items-center gap-1">
              {#each [7, 14, 30, 90] as d}
                <button 
                  type="button"
                  class="btn btn-xs {anomalyDays === d ? 'btn-primary shadow-sm' : 'btn-ghost text-base-content/60 hover:bg-base-200'} rounded-lg font-bold"
                  onclick={() => setAnomalyRange(d)}
                  disabled={loadingAnomalies}
                >
                  {d}D
                </button>
              {/each}
              {#if loadingAnomalies}
                <RefreshCw class="w-3.5 h-3.5 animate-spin text-primary ml-1" />
              {/if}
            </div>
          </div>

          <!-- KPI Summary Strip -->
          <div class="grid grid-cols-2 sm:grid-cols-4 gap-3 py-3 border-b border-base-200/60">
            <div class="bg-base-200/30 rounded-xl px-3 py-2 border border-base-200/50">
              <span class="text-[10px] font-bold text-base-content/50 uppercase tracking-wider block">Total Detections</span>
              <span class="text-lg font-black tracking-tight text-base-content">{totalAnomaliesInWindow.toLocaleString()}</span>
            </div>
            <div class="bg-base-200/30 rounded-xl px-3 py-2 border border-base-200/50">
              <span class="text-[10px] font-bold text-base-content/50 uppercase tracking-wider block">Active Unresolved</span>
              <span class="text-lg font-black tracking-tight {anomalyData.summary?.active_anomalies > 0 ? 'text-primary' : 'text-success'}">
                {(anomalyData.summary?.active_anomalies ?? 0).toLocaleString()}
              </span>
            </div>
            <div class="bg-base-200/30 rounded-xl px-3 py-2 border border-base-200/50">
              <span class="text-[10px] font-bold text-base-content/50 uppercase tracking-wider block">Resolution Rate</span>
              <span class="text-lg font-black tracking-tight text-success">
                {anomalyData.summary?.resolution_rate_pct != null ? `${anomalyData.summary.resolution_rate_pct}%` : '100%'}
              </span>
            </div>
            <div class="bg-base-200/30 rounded-xl px-3 py-2 border border-base-200/50">
              <span class="text-[10px] font-bold text-base-content/50 uppercase tracking-wider block">Primary Engine</span>
              <span class="text-xs font-bold text-base-content truncate block mt-1" title={anomalyData.models?.[0]?.label || 'Isolation Forest'}>
                {anomalyData.models?.[0]?.label ? anomalyData.models[0].label.replace(' Outliers', '') : 'Isolation Forest'}
              </span>
            </div>
          </div>

          <!-- Chart Area or Fleet Health Status -->
          <div class="h-[280px] mt-2">
            {#if totalAnomaliesInWindow > 0}
              <DeviceAnomaliesChart data={anomalyStats} viewMode={anomalyViewMode} />
            {:else}
              <!-- 100% In-Baseline Sentry State -->
              <div class="h-full w-full flex flex-col items-center justify-center p-6 text-center gap-3 border border-dashed border-base-300 rounded-2xl bg-base-200/20">
                <div class="w-12 h-12 rounded-2xl bg-success/10 border border-success/30 flex items-center justify-center text-success shadow-inner">
                  <ShieldCheck class="w-6 h-6" />
                </div>
                <div class="max-w-md">
                  <h4 class="text-sm font-bold text-base-content">Fleet Operating 100% Within Baseline</h4>
                  <p class="text-xs text-base-content/60 mt-1">
                    Zero anomalous flow deviations, burst spikes, or night creep leaks detected in the last {anomalyDays} days across your portfolio.
                  </p>
                </div>
                <!-- Active Sentry Engines Pill Row -->
                <div class="flex flex-wrap items-center justify-center gap-2 mt-1">
                  <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold bg-base-200 text-base-content/70 border border-base-300">
                    <span class="w-1.5 h-1.5 rounded-full bg-success"></span>
                    Isolation Forest Sentry
                  </span>
                  <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold bg-base-200 text-base-content/70 border border-base-300">
                    <span class="w-1.5 h-1.5 rounded-full bg-success"></span>
                    Surge & Burst Monitor
                  </span>
                  <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold bg-base-200 text-base-content/70 border border-base-300">
                    <span class="w-1.5 h-1.5 rounded-full bg-success"></span>
                    Night Creep Detection
                  </span>
                  <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold bg-base-200 text-base-content/70 border border-base-300">
                    <span class="w-1.5 h-1.5 rounded-full bg-success"></span>
                    Zero Flow Blockage
                  </span>
                </div>
                {#if anomalyData.latest_detected_at && anomalyDays < 30}
                  <div class="mt-2 pt-2 border-t border-base-200/60 w-full flex items-center justify-center gap-2">
                    <span class="text-[11px] text-base-content/50">Historical anomalies recorded in October:</span>
                    <button 
                      type="button"
                      class="btn btn-xs btn-outline btn-primary font-bold rounded-lg"
                      onclick={() => setAnomalyRange(30, true)}
                    >
                      View 30-Day Activity
                    </button>
                  </div>
                {/if}
              </div>
            {/if}
          </div>
        </GlassCard>
      </div>

      <!-- Live Sentry Stream / Recent Alerts -->
      <div>
        <GlassCard title="Active Sentry Stream" subtitle="Most recent alarms requiring review" class="p-6">
          {#if recentAlerts.length > 0}
            <div class="space-y-3 mt-2">
              {#each recentAlerts as alert}
                <a 
                  href={deviceHref(alert.device_eui)}
                  class="flex items-start gap-3 p-3 rounded-xl bg-base-content/[0.02] border border-base-content/5 hover:border-primary/20 hover:bg-base-content/[0.04] transition-all group cursor-pointer"
                >
                  <div class="p-2 rounded-lg bg-error/10 text-error shrink-0">
                    <TriangleAlert class="w-5 h-5" />
                  </div>
                  <div class="flex-1 min-w-0">
                    <div class="flex items-center justify-between gap-2 mb-0.5">
                      <p class="text-xs font-black text-base-content/90 truncate leading-tight uppercase tracking-wide group-hover:text-primary transition-colors">{alert.device_name || alert.device_eui}</p>
                      <span class="text-[9px] font-bold text-base-content/20 whitespace-nowrap uppercase">{getRelativeTime(alert.last_seen)}</span>
                    </div>
                    <p class="text-[10px] text-base-content/40 font-bold truncate mb-2">
                      {alert.property_name || 'Unassigned Property'}
                    </p>
                    <div class="flex flex-wrap gap-1">
                       {#if alert.flow_alarm > 0}
                          <span class="px-2 py-0.5 bg-error text-error-content text-[8px] font-black rounded uppercase tracking-tighter">{formatFlowAlarm(alert.flow_alarm_flags, alert.flow_alarm)}</span>
                       {/if}
                       {#each alert.malfunction_alarms || [] as malf}
                          <span class="px-2 py-0.5 bg-base-content/10 text-base-content/60 text-[8px] font-black rounded uppercase tracking-tighter italic border border-base-content/5">
                             {malfunctionLabel(malf)}
                          </span>
                       {/each}
                    </div>
                  </div>
                </a>
              {/each}
            </div>
          {:else}
            <div class="py-12 flex flex-col items-center text-center space-y-3 px-6">
              <div class="w-16 h-16 rounded-full bg-base-content/[0.02] border border-base-content/10 flex items-center justify-center text-base-content/10">
                <Clock class="w-8 h-8" />
              </div>
              <div class="space-y-1">
                <p class="text-sm font-bold text-base-content/80">No active alerts reported</p>
                <p class="text-[11px] text-base-content/30 font-medium">No active alerts were returned for this portfolio.</p>
              </div>
            </div>
          {/if}
        </GlassCard>
      </div>

    </div>

    <!-- Managed Properties Roster Section -->
    <div class="mt-8">
      <GlassCard title="Managed Properties" subtitle="Quick access to building workspaces, zones, and water audits" class="p-6">
        <div class="flex items-center justify-between mb-4">
          <div class="text-xs font-bold text-base-content/60">
            Showing {Math.min(properties.length, 6)} of {properties.length} properties
          </div>
          {#if properties.length > 6}
            <a href="#/properties" class="btn btn-ghost btn-xs gap-1 text-primary font-bold text-xs">
              <span>View All Properties ({properties.length})</span>
              <ArrowRight class="w-3.5 h-3.5" />
            </a>
          {/if}
        </div>

        {#if properties.length === 0}
          <div class="text-center py-12 text-base-content/40 text-xs">
            No properties currently assigned to this account.
          </div>
        {:else}
          <div class="overflow-x-auto">
            <table class="table table-sm w-full">
              <thead>
                <tr class="border-b border-base-content/10 text-[10px] font-black uppercase tracking-wider text-base-content/50">
                  <th class="py-2.5">Property</th>
                  <th>Type</th>
                  <th class="text-center">Units</th>
                  <th class="text-center">Devices</th>
                  <th class="text-center">Status</th>
                  <th class="text-right pr-4">Action</th>
                </tr>
              </thead>
              <tbody>
                {#each properties.slice(0, 6) as p (p.property_id)}
                  <tr class="border-b border-base-content/5 hover:bg-base-content/[0.02] transition-colors group">
                    <td class="py-3">
                      <div class="flex items-center gap-3">
                        <div class="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 border border-base-content/10 {p.property_type === 'multi_family' ? 'bg-indigo-500/10 text-indigo-500' : 'bg-emerald-500/10 text-emerald-500'}">
                          {#if p.property_type === 'multi_family'}
                            <Building2 class="w-4 h-4" />
                          {:else}
                            <House class="w-4 h-4" />
                          {/if}
                        </div>
                        <div class="min-w-0">
                          <a href="#/properties/{p.property_id}" class="font-black text-xs text-base-content/90 hover:text-primary transition-colors block truncate">
                            {p.property_name || `Property #${p.property_id}`}
                          </a>
                          <div class="text-[10px] text-base-content/40 font-medium truncate">
                            {[p.address, p.city, p.state].filter(Boolean).join(', ') || `ID #${p.property_id}`}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span class="inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider {
                        p.property_type === 'multi_family' ? 'bg-indigo-500/10 text-indigo-500 border border-indigo-500/20' :
                        'bg-cyan-500/10 text-cyan-600 border border-cyan-500/20'
                      }">
                        {(p.property_type || 'single_family').replace('_', ' ')}
                      </span>
                    </td>
                    <td class="text-center font-bold text-xs text-base-content/80">
                      {(p.unit_count || p.occupancy_count || scopedGisProperties.find(b => Number(b.property_id) === Number(p.property_id))?.total_units || 0).toLocaleString()}
                    </td>
                    <td class="text-center font-bold text-xs text-base-content/80">
                      {(p.device_count || scopedGisProperties.find(b => Number(b.property_id) === Number(p.property_id))?.total_monitored_devices || 0).toLocaleString()}
                    </td>
                    <td class="text-center">
                      {#if (allAlerts.filter(a => a.property_id === p.property_id).length) > 0}
                        <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider bg-error/10 text-error border border-error/20">
                          <TriangleAlert class="w-3 h-3" />
                          {allAlerts.filter(a => a.property_id === p.property_id).length} Active
                        </span>
                      {:else}
                        <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider bg-success/10 text-success border border-success/20">
                          <CheckCircle2 class="w-3 h-3" />
                          Normal
                        </span>
                      {/if}
                    </td>
                    <td class="text-right pr-4">
                      <a href="#/properties/{p.property_id}" class="btn btn-ghost btn-xs gap-1 text-primary hover:bg-primary/10 rounded-lg font-bold text-xs">
                        <span>Workspace</span>
                        <ArrowRight class="w-3 h-3 transition-transform group-hover:translate-x-0.5" />
                      </a>
                    </td>
                  </tr>
                {/each}
              </tbody>
            </table>
          </div>
        {/if}
      </GlassCard>
    </div>
  {/if}

  <FleetPolicyBroadcastModal bind:isOpen={isPolicyModalOpen} properties={scopedGisProperties} onBroadcastSuccess={() => loadDashboard(true)} />
  <PortfolioGISHeatmapModal bind:isOpen={isGisModalOpen} customProperties={scopedGisProperties} />
</div>
